import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// Contest config
const CONTEST_START = new Date("2026-03-21T00:00:00Z");
const CONTEST_END = new Date(CONTEST_START.getTime() + 90 * 24 * 60 * 60 * 1000); // 90 days

export interface LeaderboardEntry {
  user_id: string;
  wallet_address: string;
  display_name: string | null;
  avatar_url: string | null;
  total_mint_days: number;
  total_referrals: number;
  total_score: number;
}

export interface ReferralDetail {
  referred_wallet: string;
  converted_at: string;
}

export interface UserContestStats {
  hasMintedToday: boolean;
  referralCode: string | null;
  totalMintDays: number;
  totalReferrals: number;
  totalScore: number;
  rank: number | null;
  referredBy: string | null; // wallet of the person who referred this user
  referralDetails: ReferralDetail[]; // list of users this person referred
}

function generateReferralCode(userId: string, walletAddress: string): string {
  const userFrag = userId.replace(/-/g, "").slice(0, 4);
  const walletFrag = walletAddress.slice(0, 4);
  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const dateHash = today.replace(/-/g, "").slice(4); // MMDD
  return `FLOW-${userFrag}-${walletFrag}-${dateHash}`;
}

export function useAirdropChallenge(userId?: string, walletAddress?: string) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userStats, setUserStats] = useState<UserContestStats>({
    hasMintedToday: false,
    referralCode: null,
    totalMintDays: 0,
    totalReferrals: 0,
    totalScore: 0,
    rank: null,
    referredBy: null,
    referralDetails: [],
  });
  const [loading, setLoading] = useState(true);

  const contestActive = new Date() >= CONTEST_START && new Date() <= CONTEST_END;
  const contestEnded = new Date() > CONTEST_END;

  const timeRemaining = useCallback(() => {
    const now = new Date().getTime();
    const end = CONTEST_END.getTime();
    const diff = Math.max(0, end - now);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { days, hours, minutes, total: diff };
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    const { data, error } = await supabase
      .from("airdrop_leaderboard")
      .select("*")
      .order("total_score", { ascending: false })
      .limit(20);

    if (!error && data) {
      setLeaderboard(data as unknown as LeaderboardEntry[]);
    }
  }, []);

  const fetchUserStats = useCallback(async () => {
    if (!userId) return;

    // Check if minted today
    const today = new Date().toISOString().split("T")[0];
    const { data: todayEntry } = await supabase
      .from("airdrop_contest_entries")
      .select("referral_code")
      .eq("user_id", userId)
      .eq("mint_date", today)
      .maybeSingle();

    // Get total mint days
    const { count: mintDays } = await supabase
      .from("airdrop_contest_entries")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId);

    // Get total referrals
    const { count: referrals } = await supabase
      .from("airdrop_referrals")
      .select("*", { count: "exact", head: true })
      .eq("referrer_user_id", userId);

    // Get referral details (who this user referred)
    const { data: refDetails } = await supabase
      .from("airdrop_referrals")
      .select("referred_wallet, converted_at")
      .eq("referrer_user_id", userId)
      .order("converted_at", { ascending: false });

    // Check if this user was referred by someone
    const { data: referredByEntry } = await supabase
      .from("airdrop_referrals")
      .select("referrer_user_id")
      .eq("referred_user_id", userId)
      .maybeSingle();

    let referredByWallet: string | null = null;
    if (referredByEntry) {
      // Get referrer's wallet from their contest entries
      const { data: referrerEntry } = await supabase
        .from("airdrop_contest_entries")
        .select("wallet_address")
        .eq("user_id", referredByEntry.referrer_user_id)
        .limit(1)
        .maybeSingle();
      referredByWallet = referrerEntry?.wallet_address ?? null;
    }

    const totalRefs = referrals ?? 0;
    const totalDays = mintDays ?? 0;
    const score = totalRefs * 10 + totalDays;

    // Get rank from leaderboard
    const { data: lb } = await supabase
      .from("airdrop_leaderboard")
      .select("user_id, total_score")
      .order("total_score", { ascending: false });

    let rank: number | null = null;
    if (lb) {
      const idx = lb.findIndex((e: any) => e.user_id === userId);
      if (idx >= 0) rank = idx + 1;
    }

    setUserStats({
      hasMintedToday: !!todayEntry,
      referralCode: todayEntry?.referral_code ?? null,
      totalMintDays: totalDays,
      totalReferrals: totalRefs,
      totalScore: score,
      rank,
      referredBy: referredByWallet,
      referralDetails: (refDetails as ReferralDetail[]) ?? [],
    });
  }, [userId]);

  // Register a mint for the contest
  const registerContestMint = useCallback(
    async (nftId: string) => {
      if (!userId || !walletAddress || !contestActive) return null;

      const today = new Date().toISOString().split("T")[0];

      // Check if already registered today
      const { data: existing } = await supabase
        .from("airdrop_contest_entries")
        .select("referral_code")
        .eq("user_id", userId)
        .eq("mint_date", today)
        .maybeSingle();

      if (existing) return existing.referral_code;

      const code = generateReferralCode(userId, walletAddress);
      const { error } = await supabase.from("airdrop_contest_entries").insert({
        user_id: userId,
        wallet_address: walletAddress,
        mint_date: today,
        nft_id: nftId,
        referral_code: code,
      });

      if (!error) {
        // Check if there's a stored referral code
        const storedRef = localStorage.getItem("airdrop_ref");
        // Always clear localStorage immediately to prevent re-processing
        if (storedRef) {
          localStorage.removeItem("airdrop_ref");

          // Check if this is user's first contest entry ever
          const { count } = await supabase
            .from("airdrop_contest_entries")
            .select("*", { count: "exact", head: true })
            .eq("user_id", userId);

          if (count === 1) {
            // First mint ever — convert the referral
            // Find who owns this referral code
            const { data: refEntry } = await supabase
              .from("airdrop_contest_entries")
              .select("user_id")
              .eq("referral_code", storedRef)
              .limit(1)
              .maybeSingle();

            if (refEntry && refEntry.user_id !== userId) {
              // Insert referral — unique constraint prevents duplicates
              const { error: refError } = await supabase.from("airdrop_referrals").insert({
                referrer_user_id: refEntry.user_id,
                referred_user_id: userId,
                referred_wallet: walletAddress,
                referral_code: storedRef,
              });

              if (!refError) {
                // Referral successfully recorded
                return code;
              }
              // If unique constraint violation (23505), referral already exists — that's fine
              if (refError && (refError as any).code !== "23505") {
                console.error("Referral insert error:", refError);
              }
            }
          }
        }

        setUserStats((prev) => ({
          ...prev,
          hasMintedToday: true,
          referralCode: code,
          totalMintDays: prev.totalMintDays + 1,
          totalScore: prev.totalScore + 1,
        }));
        return code;
      }
      return null;
    },
    [userId, walletAddress, contestActive]
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLeaderboard(), fetchUserStats()]).finally(() =>
      setLoading(false)
    );
  }, [fetchLeaderboard, fetchUserStats]);

  return {
    leaderboard,
    userStats,
    loading,
    contestActive,
    contestEnded,
    contestStart: CONTEST_START,
    contestEnd: CONTEST_END,
    timeRemaining,
    registerContestMint,
    refreshLeaderboard: fetchLeaderboard,
  };
}
